import { useQuery } from '@common/hooks';
import { useCurrentEntity } from '@identity/hooks';
import { Alert, FormInstance, Skeleton } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { datetime } from '@common/services/datetime';
import { AutoAnimatedHeight } from 'works/legacy/components/auto-animated-height';
import {
  Currency,
  DatePicker,
  DocumentPreviewModalDeprecatedProvider,
  ErrorHandler,
  Form,
  Col,
  Row,
} from '@common/components';
import {
  DisputeBillingRateInput,
  DisputeCommentInput,
  DisputeDiffSectionLabels,
  DisputeFilesInput,
  DisputeHoursInput,
  DisputePayCodeInput,
  DuplicatedDisputeAlert,
  RedoActionButton,
  ValuesOnFinishType,
} from '@pay-bill/components';
import {
  AssignmentInfosQuery,
  AssignmentInfosQueryArgs,
  AssignmentInfosQueryResponse,
  DISPUTE_PREVIEW_INFO_QUERY,
  InvoiceReviewQuery,
  InvoiceReviewQueryArgs,
  InvoiceReviewQueryResponse,
  RevInvoiceReviewQuery,
} from '@pay-bill/graphql';
import { useHasValidMissingLineDisputeFields } from '@pay-bill/hooks';
import { AssignmentSelect } from './assignment-select';
import { Store } from 'antd/lib/form/interface';
import { debounce } from 'lodash';
import { Colors, FontWeight } from '@common/tokens';

export const DisputeCreateMissingLineDrawerBody = ({
  invOrRinvId = '',
  onFinish,
  baseErrors,
  form,
  setLoading,
}: {
  form: FormInstance;
  baseErrors: React.ReactNode;
  invOrRinvId?: string;
  onFinish: (values: ValuesOnFinishType) => void;
  setLoading: (value: boolean) => void;
}) => {
  const currentEntity = useCurrentEntity();
  const entityType = currentEntity.entityType;

  const [formValues, setFormValues] = useState({
    newHours: '0',
    newPayCode: null as null | string,
    newDate: null as null | string,
    finalRateCents: undefined as undefined | number,
    staffingRequestAssignmentId: null as null | string,
  });
  const [isCustomRateCents, setIsCustomRateCents] = useState<boolean>(false);

  const { data: reverseInvoiceData } = useQuery(RevInvoiceReviewQuery, {
    skip: !invOrRinvId,
    variables: { id: invOrRinvId },
  });
  const { data: invoiceData } = useQuery<
    InvoiceReviewQueryResponse,
    InvoiceReviewQueryArgs
  >(InvoiceReviewQuery, {
    skip: !invOrRinvId,
    variables: { id: invOrRinvId },
  });

  const invoice =
    entityType === 'Supplier'
      ? reverseInvoiceData?.worksReverseInvoice?.worksInvoice
      : invoiceData?.worksInvoice;

  const options = useQuery<
    AssignmentInfosQueryResponse,
    AssignmentInfosQueryArgs
  >(AssignmentInfosQuery, {
    variables: {
      organizationId: invoice?.organization.id,
      startDateBefore: invoice?.payPeriodEndDate,
      endDateAfter: invoice?.payPeriodStartDate,
    },
  });

  const orderedAssignments = React.useMemo(() => {
    if (!options.data?.assignments) return [];

    return [...options.data.assignments].sort((aa, bb) => {
      return aa.worksProfile.firstName.localeCompare(bb.worksProfile.firstName);
    });
  }, [options.data?.assignments]);

  const previewVariables = useMemo(() => {
    if (
      isNaN(parseFloat(formValues.newHours)) ||
      !formValues.newDate ||
      !formValues.newPayCode ||
      !formValues.staffingRequestAssignmentId
    ) {
      return null;
    }

    return {
      newHours: parseFloat(formValues.newHours),
      newDate: formValues.newDate,
      newPayCode: formValues.newPayCode,
      finalRateCents: formValues.finalRateCents
        ? Math.floor(formValues.finalRateCents * 100)
        : undefined,
      staffingRequestAssignmentId: formValues.staffingRequestAssignmentId,
    };
  }, [
    formValues.newHours,
    formValues.newDate,
    formValues.staffingRequestAssignmentId,
    formValues.newPayCode,
    formValues.finalRateCents,
  ]);

  const { data: previewData, loading: loadingPreview } = useQuery(
    DISPUTE_PREVIEW_INFO_QUERY,
    {
      skip: !previewVariables,
      variables: previewVariables!,
    }
  );

  const { previewRate, disputeMatches } = previewData || {};
  const hasDuplication = !!(
    disputeMatches?.dispute ||
    disputeMatches?.invoiceItem ||
    disputeMatches?.workerTimesheet
  );

  const calculatedRate = previewRate?.finalRateCents || 0;
  const calculatedFinalRate = previewRate?.billingAmountCents || 0;
  const rateCents = isCustomRateCents
    ? formValues.finalRateCents
    : previewRate?.finalRateCents;

  const formInvalid = !useHasValidMissingLineDisputeFields(form, formValues);

  useEffect(() => {
    setLoading(loadingPreview || hasDuplication || formInvalid);
  }, [loadingPreview, hasDuplication, formInvalid, setLoading]);

  useEffect(() => {
    if (isCustomRateCents) {
      return;
    }

    const formattedRate = parseFloat(((rateCents || 0) / 100).toFixed(2));

    form.setFieldValue('finalRateCents', formattedRate);
  }, [form, rateCents, isCustomRateCents]);

  if (options.error) {
    return (
      <ErrorHandler
        log="CreateMissingLineDisputeDrawer"
        error={options.error}
      />
    );
  }

  const onFormChange = debounce(
    (
      changes: {
        newHours: string;
        newPayCode: string | null;
        newDate: string | null;
        finalRateCents: number | undefined;
        staffingRequestAssignmentId: string | null;
      },
      // FIXME: antd upgrade compatibility, was Record<string, any> before.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formValues: Record<string, any>
    ) => {
      const updatedValues = {
        ...formValues,
        ...changes,
      };

      if (changes.newPayCode) {
        updatedValues.finalRateCents = undefined;
      }

      if (Array.isArray(updatedValues.newHours)) {
        updatedValues.newHours = updatedValues.newHours[1];
      }

      setFormValues(updatedValues);
    },
    500
  );

  const onFormFinish = (values: Store) => {
    onFinish({
      worksInvoiceItemId: undefined,
      staffingRequestAssignmentId: values.staffingRequestAssignmentId,
      invOrRinvId: invOrRinvId,
      entityType: entityType,
      files: values.files || [],
      input: {
        newHours: values.newHours,
        newDate: values.newDate,
        newPayCode: values.newPayCode,
        creationReason: values.comment,
        finalRateCents: values.finalRateCents * 100,
        disputeType: 'missing_line',
      },
    });
  };

  return (
    <>
      <p className="dispute-instructions">
        Enter correct information for the hours worked missing from the invoice.
        Resolved disputes will appear on the next invoice that&apos;s created.
      </p>
      <Form
        onFinish={onFormFinish}
        layout="vertical"
        form={form}
        onValuesChange={(changes, formValues) =>
          onFormChange(changes, formValues)
        }
      >
        <Row gutter={[16, 16]}>
          <Col span={16}>
            {options.loading || !options.data ? (
              <Form.Item label="Name" required tooltip="Loading...">
                <Skeleton.Input active block size="small" />
              </Form.Item>
            ) : (
              <AssignmentSelect assignments={orderedAssignments} />
            )}
          </Col>
          <Col span={8}>
            <Form.Item
              label="Date"
              name="newDate"
              rules={[
                {
                  required: true,
                  message: 'Please enter the date',
                },
              ]}
            >
              <DatePicker
                placeholder="mm/dd/yyyy"
                format="MM/DD/YYYY"
                style={{ width: '100%' }}
                size="large"
                defaultPickerValue={datetime(invoice?.payPeriodStartDate)}
                disabledDate={(currentDate) => {
                  const start = datetime(invoice?.payPeriodStartDate);
                  const end = datetime(invoice?.payPeriodEndDate);

                  return (
                    !!currentDate &&
                    (currentDate.isBefore(start) || currentDate.isAfter(end))
                  );
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <AutoAnimatedHeight visible={!!baseErrors}>
          <Row style={{ paddingBottom: 24 }}>
            <div className="dup-alert-row">
              <Alert showIcon message={baseErrors} type="warning" />
            </div>
          </Row>
        </AutoAnimatedHeight>
        <DisputeDiffSectionLabels mode="missing-line-form" />
        <Row gutter={16} style={{ paddingTop: 4 }}>
          <Col span={6}>
            <DisputePayCodeInput
              payCodes={invoice?.organization.payCodes || []}
              onSelect={() => {
                if (isCustomRateCents) {
                  setIsCustomRateCents(false);
                }
              }}
            />
          </Col>
          <Col span={6}>
            <Row id="billing-rate-col">
              <DisputeBillingRateInput
                disabled={!calculatedRate}
                onSetRateCents={() => setIsCustomRateCents(true)}
              />
              {isCustomRateCents && (
                <RedoActionButton
                  tooltipTitle="Click to reset Billing Rate to the default Pay Code rate"
                  onRedoAction={() => {
                    setIsCustomRateCents(false);
                    setFormValues({
                      ...formValues,
                      finalRateCents: undefined,
                    });
                  }}
                />
              )}
            </Row>
          </Col>
          <Col span={6}>
            <DisputeHoursInput form={form} />
          </Col>
          <Col
            span={6}
            className="billing-amount-col"
            style={{ paddingTop: 6 }}
          >
            <Currency value={calculatedFinalRate / 100} />
            {invoice?.preCalculated && '*'}
          </Col>
        </Row>
        <AutoAnimatedHeight visible={isCustomRateCents}>
          <Alert
            showIcon
            message="You're setting a different Billing Rate for this Pay Code."
            type="info"
            style={{
              backgroundColor: Colors.skyHyperLight,
              border: 0,
            }}
          />
        </AutoAnimatedHeight>
        {invoice?.preCalculated && (
          <p className="estimation-message">
            * Billing Amount is an estimate and may differ from the final
            amount.
          </p>
        )}
        <AutoAnimatedHeight visible={hasDuplication}>
          <div className="dup-alert-row">
            <DuplicatedDisputeAlert
              dispute={disputeMatches?.dispute}
              invoiceItem={disputeMatches?.invoiceItem}
            />
          </div>
        </AutoAnimatedHeight>
        <DisputeCommentInput creationType />
        <Row className="dispute-upload-row">
          <Col span={24}>
            <DocumentPreviewModalDeprecatedProvider>
              <DisputeFilesInput maxFilesSlots={3} />
            </DocumentPreviewModalDeprecatedProvider>
          </Col>
        </Row>
      </Form>
      <style jsx>
        {`
          .estimation-message {
            padding-top: 12px;
            margin-bottom: 0px;
          }
          .dup-alert-row {
            padding-top: 12px;
          }
          .dispute-instructions {
            margin-bottom: 40px;
          }
          :global(.select-option div) {
            white-space: break-spaces;
            word-break: break-word;
          }
          :global(.missing-line-body .billing-rate-col) {
            padding-top: 5px;
          }
          :global(.missing-line-body .dispute-label),
          :global(.missing-line-body .ant-form-item-label) {
            font-weight: ${FontWeight.bold};
          }
          :global(.missing-line-body .ant-form-item) {
            margin-bottom: 16px;
          }
          :global(.dispute-upload-row) {
            margin-top: 8px;
            max-width: 100%;
          }
        `}
      </style>
    </>
  );
};
